import React from "react";
import FloatingButton from "../ui/FloatingButton";
import EntityCard from "../ui/EntityCard";
import Matchup, { matchSides } from "../ui/Matchup";
import { eventTimeZone, formatEventDateTime } from "../../utils/eventTime";

// One flat match card: both sides broadcast style (logo or player photo, name
// below, VS between, score under each side once play has started), the shared
// status badge, then when, venue and court.
const MatchCard = ({
  match,
  onViewMatch,
  onStartMatch,
  allowScoring = false,
}) => {
  // At the venue's local time (utils/eventTime)
  const formatDate = (dateString) => {
    if (!dateString) return "TBD";
    return formatEventDateTime(dateString, eventTimeZone(match));
  };

  const getParticipantScore = (participantKey) => {
    // Support both scoreData (camelCase) and score_data (snake_case)
    const scoreData = match.scoreData || match.score_data;
    if (!scoreData) return 0;

    const toNum = (v) => {
      if (v == null) return 0;
      if (typeof v === 'number') return v;
      if (typeof v === 'object') {
        const inner = v.total ?? v.score ?? v.value ?? 0;
        return typeof inner === 'number' ? inner : 0;
      }
      return Number(v) || 0;
    };

    return toNum(scoreData[participantKey]);
  };

  const config = (() => {
    const cfg = match.matchConfig || match.match_config;
    if (typeof cfg !== 'string') return cfg || {};
    try { return JSON.parse(cfg) || {}; } catch { return {}; }
  })();

  // Drop-in team names come from match_config.drop_in_teams (matchSides)
  const [home, away] = matchSides(match);
  const winnerId = match.status === 'completed' ? (match.winnerId || match.winner_id) : null;
  const showScore = !['scheduled', 'ready', 'pending', 'cancelled', 'canceled'].includes(match.status || 'scheduled');
  const sideOf = (side, key) => ({
    ...side,
    winner: winnerId != null && side.id != null && String(winnerId) === String(side.id),
    score: showScore ? getParticipantScore(key) : undefined,
  });

  const title = (() => {
    const stored = match.matchName || match.match_name || '';
    const p1 = match.participant1Name || match.participant_1_name;
    const p2 = match.participant2Name || match.participant_2_name;
    // If the name still has stale "Participant N" placeholders, rebuild from real names
    if (p1 && p2 && /Participant \d+/i.test(stored)) {
      const tournament = match.tournamentName || match.tournament_name;
      return tournament ? `${tournament}: ${p1} vs ${p2}` : `${p1} vs ${p2}`;
    }
    return stored || `${match.sport_name || 'Unknown Sport'} Match`;
  })();

  const court = match.court || config.court_name;
  const hostFirst = match.creatorFirstName || match.creator_first_name;
  const host = hostFirst ? `${hostFirst} ${match.creatorLastName || match.creator_last_name || ''}`.trim() : 'N/A';
  const tournamentName = match.tournament_name || match.tournamentName;

  return (
    <EntityCard
      testId="match-card"
      className="match-list-card"
      title={title}
      feature={<Matchup home={sideOf(home, 'participant1')} away={sideOf(away, 'participant2')} />}
      badges={[
        { status: match.status || 'scheduled', kind: 'match' },
        { label: match.sport_name || 'Unknown Sport', tone: 'sport' },
      ]}
      rows={[
        { key: 'when', icon: '⏰', label: 'When', value: formatDate(match.scheduledAt || match.scheduled_at) },
        { key: 'venue', icon: '🏟️', label: 'Venue', value: [match.venue || 'Not decided yet', court].filter(Boolean).join(' · ') },
        tournamentName && { key: 'tournament', icon: '🏅', label: 'Tournament', value: tournamentName },
        { key: 'host', icon: '👑', label: 'Host', value: host },
      ].filter(Boolean)}
      actions={match.status === 'scheduled' && allowScoring ? (
        <FloatingButton
          variant="primary"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onStartMatch?.(match);
          }}
          icon="▶️"
        >
          Start Match
        </FloatingButton>
      ) : null}
      onClick={() => onViewMatch(match)}
    />
  );
};

export default MatchCard;
